import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel } from './ui/UI';
/**
 * Product Jobs — what Periscan does, in five sentences.
 *
 * Deliberately typography-forward. No icon boxes, no tilt-on-hover, no
 * floating cards. Each "job" is a numbered editorial entry with the
 * verb-first title and one plainspoken sentence. Reads like a printed
 * product manifest — not a SaaS feature grid.
 */
const jobs = [{
  title: 'Validate exposure.',
  body: 'See which exposures are reachable, exploitable, blocked, detected, mitigated, or not worth panic.'
}, {
  title: 'Test controls.',
  body: 'Check whether defenses detect, block, log, alert, and route the activity they are supposed to catch.'
}, {
  title: 'Find attack paths.',
  body: 'Connect cloud, identity, SaaS, code, secrets, AI apps, and internal systems into realistic paths to impact.'
}, {
  title: 'Verify fixes.',
  body: 'Re-test after remediation. A closed ticket is not a closed risk.'
}, {
  title: 'Produce evidence.',
  body: 'Generate proof for security teams, executives, customers, auditors, insurers, MSSPs, and boards.'
}];
export function WhatPeriscanValidates() {
  return <section id="product-jobs" aria-labelledby="jobs-heading" className="relative w-full py-32 border-t border-white/5" data-id="element-1400">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1401">
        <SectionLabel number="—" label="Product · One platform, five jobs" className="mb-8" data-id="element-1402" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-1403">
          <div className="lg:col-span-7" data-id="element-1404">
            <motion.h2 id="jobs-heading" initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[1.05] text-white" data-id="element-1405">
              One platform.
              <br data-id="element-1406" />
              <span className="text-white/40" data-id="element-1407">Five validation jobs.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-1408">
            <p className="text-lg text-white/65 leading-relaxed" data-id="element-1409">
              No icon grid. No "AI-powered" hand-waving. Five specific things
              the product does, named with verbs.
            </p>
          </div>
        </div>

        {/* Editorial numbered list — no card chrome, no icons, no tilt. */}
        <ol className="border-t border-white/5" data-id="element-1410">
          {jobs.map((job, i) => <motion.li key={job.title} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="border-b border-white/5 py-10 grid grid-cols-12 gap-6 items-baseline" data-id="element-1411">
              <span className="col-span-12 md:col-span-1 font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em]" data-id="element-1412">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="col-span-12 md:col-span-4 text-2xl md:text-3xl text-white font-semibold tracking-tight leading-snug" data-id="element-1413">
                {job.title}
              </h3>
              <p className="col-span-12 md:col-span-7 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl" data-id="element-1414">
                {job.body}
              </p>
            </motion.li>)}
        </ol>
      </div>
    </section>;
}